/**
 * PaymentOption Component
 * Single payment option dengan radio button
 */

import { Box, Flex, Image, Radio } from '@chakra-ui/react';
import { Text } from '@diamond/shared/ui';
import { UseFormRegister } from 'react-hook-form';

export interface PaymentOptionProps {
  value: string;
  title: string;
  description: string;
  badge?: {
    text: string;
    variant: 'cod' | 'va';
  };
  logoSrc?: string;
  isSelected: boolean;
  isDisabled?: boolean;
  onChange: (value: string) => void;
  register: UseFormRegister<any>;
}

/**
 * PaymentOption Component
 * Menampilkan satu opsi pembayaran dengan radio button
 */
export function PaymentOption({
  value,
  title,
  description,
  badge,
  logoSrc,
  isSelected,
  isDisabled = false,
  onChange,
}: PaymentOptionProps) {
  // Gradient border sama dengan VoucherSection
  const gradientBorder =
    'linear-gradient(25.45deg, #FF1E1E -6.15%, rgba(36, 81, 206, 0.9) 48.07%, rgba(90, 200, 255, 0.8) 94.92%)';

  const handleChange = () => {
    if (isDisabled) {
      return;
    }
    onChange(value);
  };

  const radioBorderColor = (() => {
    if (isDisabled) {
      return 'gray.300';
    }
    if (isSelected) {
      return 'white';
    }
    return 'gray.300';
  })();

  const radioBackground = (() => {
    if (isDisabled) {
      return 'gray.100';
    }
    if (isSelected) {
      return gradientBorder;
    }
    return 'transparent';
  })();

  // Content yang akan di-render (shared untuk selected dan unselected)
  const content = (
    <Flex align="center" justify="space-between" gap={4} w="full">
      {/* Left: Title + Description */}
      <Box flex={1}>
        <Text
          fontSize="md"
          fontWeight="bold"
          color={(() => {
            if (isSelected) {
              return 'blue.700';
            }
            if (isDisabled) {
              return 'gray.400';
            }
            return 'gray.900';
          })()}
          mb={1}
        >
          {title}
        </Text>
        <Text fontSize="sm" color={isDisabled ? 'gray.400' : 'gray.600'}>
          {description}
        </Text>
      </Box>

      {/* Middle: Badge or Logo */}
      {badge && (
        <Box
          px={3}
          py={1}
          border="1px"
          borderColor={isDisabled ? 'gray.300' : 'red.500'}
          borderRadius="md"
          flexShrink={0}
          opacity={isDisabled ? 0.5 : 1}
        >
          <Text
            fontSize="xs"
            fontWeight="bold"
            color={isDisabled ? 'gray.400' : 'red.500'}
            letterSpacing="wide"
          >
            {badge.text}
          </Text>
        </Box>
      )}

      {logoSrc && !badge && (
        <Box flexShrink={0} w="78px" h="auto" opacity={isDisabled ? 0.45 : 1}>
          <Image
            src={logoSrc}
            alt={title}
            objectFit="contain"
            w="full"
            h="auto"
          />
        </Box>
      )}

      {/* Right: Radio Button Circle */}
      <Box position="relative" flexShrink={0}>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="24px"
          h="24px"
          borderRadius="full"
          borderWidth="2px"
          borderColor={radioBorderColor}
          background={radioBackground}
          transition="all 0.2s"
        >
          {isSelected && !isDisabled && (
            <Box w="8px" h="8px" borderRadius="full" bg="white" />
          )}
        </Box>
        <Radio
          id={`payment-${value}`}
          value={value}
          isChecked={isSelected}
          isDisabled={isDisabled}
          onChange={handleChange}
          position="absolute"
          top={0}
          left={0}
          opacity={0}
          w="24px"
          h="24px"
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          m={0}
          aria-disabled={isDisabled}
        />
      </Box>
    </Flex>
  );

  // Selected state: gradient border wrapper
  if (isSelected && !isDisabled) {
    return (
      <Box
        as="label"
        htmlFor={`payment-${value}`}
        cursor="pointer"
        background={gradientBorder}
        borderRadius="md"
        p="2px"
        transition="all 0.2s"
        data-testid={`payment-option-${value}`}
      >
        <Box bg="white" borderRadius="md" p={4}>
          {content}
        </Box>
      </Box>
    );
  }

  // Unselected / disabled state: normal border
  return (
    <Box
      as="label"
      htmlFor={isDisabled ? undefined : `payment-${value}`}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      p={4}
      bg={isDisabled ? 'gray.50' : 'white'}
      opacity={isDisabled ? 0.7 : 1}
      transition="all 0.2s"
      pointerEvents={isDisabled ? 'none' : 'auto'}
      aria-disabled={isDisabled}
      data-testid={`payment-option-${value}`}
      data-disabled={isDisabled ? 'true' : 'false'}
      _hover={
        isDisabled
          ? undefined
          : {
              borderColor: 'gray.300',
              bg: 'gray.50',
            }
      }
    >
      {content}
    </Box>
  );
}
